import { ArrowDropDown, CloseOutlined } from "@mui/icons-material";
import { Box, Button, Menu } from "@mui/material";
import CustomCheckbox from "components/Checkbox";
import TextInput from "components/TextInput";
import { useMemo, useState, useCallback } from "react";
import styles from "./FilterMenu.styles";

const FilterMenu = ({
  options,
  optionsDesignation = [],
  optionsOtherAds = [],
  filterType,
  onApply,
  label,
  placeholder = "Select a value",
  triggerSx = {},
}) => {
  const [selected, setSelected] = useState(options);
  const [selectedDesignation, setSelectedDesignation] = useState(optionsDesignation);
  const [selectedOtherAds, setSelectedOtherAds] = useState(optionsOtherAds);
  const [anchorEl, setAnchorEl] = useState(null);

  const selectedCount = useMemo(() =>
    [...selected, ...selectedDesignation, ...selectedOtherAds].filter(item => item.isSelected).length,
    [selected, selectedDesignation, selectedOtherAds]
  );

  const isSelectAllChecked = useMemo(() =>
    selected.filter(item => item.isSelected).length === options.length,
    [selected, options.length]
  );
  const isDesignatedAllChecked = useMemo(() =>
    selectedDesignation.filter(item => item.isSelected).length === optionsDesignation.length,
    [selectedDesignation, optionsDesignation.length]
  );
  const isOtherAdsAllChecked = useMemo(() =>
    selectedOtherAds.filter(item => item.isSelected).length === optionsOtherAds.length,
    [selectedOtherAds, optionsOtherAds.length]
  );

  const toggleSelectAll = useCallback(type => {
    const map = {
      metasearch: [selected, setSelected],
      DesignationAds: [selectedDesignation, setSelectedDesignation],
      OtherAds: [selectedOtherAds, setSelectedOtherAds],
    };
    const [items, setItems] = map[type] || [];
    if (!items || !setItems) return;
    const allSelected = items.every(item => item.isSelected);
    setItems(items.map(item => ({ ...item, isSelected: !allSelected })));
  }, [selected, selectedDesignation, selectedOtherAds]);

  const handleCheckboxChange = useCallback(id => {
    setSelected(selected.map(i => (i.id === id || i.value === id) ? { ...i, isSelected: !i.isSelected } : i));
  }, [selected]);

  const handleDesignationChange = useCallback(id => {
    setSelectedDesignation(selectedDesignation.map(i => i.id === id ? { ...i, isSelected: !i.isSelected } : i));
  }, [selectedDesignation]);

  const handleOtherAdsChange = useCallback(id => {
    setSelectedOtherAds(selectedOtherAds.map(i => i.id === id ? { ...i, isSelected: !i.isSelected } : i));
  }, [selectedOtherAds]);

  const handleUnselectAll = useCallback(() => {
    setSelected(selected.map(item => ({ ...item, isSelected: false })));
    setSelectedDesignation(selectedDesignation.map(item => ({ ...item, isSelected: false })));
    setSelectedOtherAds(selectedOtherAds.map(item => ({ ...item, isSelected: false })));
  }, [selected, selectedDesignation, selectedOtherAds]);

  const handleApply = useCallback(() => {
    onApply([
      ...selected.filter(item => item.isSelected),
      ...selectedDesignation.filter(item => item.isSelected),
      ...selectedOtherAds.filter(item => item.isSelected),
    ]);
    setAnchorEl(null);
  }, [onApply, selected, selectedDesignation, selectedOtherAds]);

  const getInputValue = useCallback(() => {
    if (!selectedCount) return "";
    if (selectedCount === 1)
      return [...selected, ...selectedDesignation, ...selectedOtherAds].find(item => item.isSelected)?.label;
    return `${selectedCount} selected`;
  }, [selected, selectedDesignation, selectedOtherAds, selectedCount]);

  return (
    <>
      <TextInput
        placeholder={placeholder}
        type="text"
        readOnly
        sx={{ ...styles.textInput, ...triggerSx }}
        value={getInputValue()}
        endAdornment={<ArrowDropDown color="disabled" />}
        onClick={e => setAnchorEl(e.target)}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        sx={styles.menu}
      >
        <div style={styles.menuContainer}>
          <div style={styles.menuHeader}>
            <span>{filterType}</span>
            <span onClick={() => setAnchorEl(null)}>
              <CloseOutlined sx={styles.closeIcon} />
            </span>
          </div>
          <div style={styles.menuBody}>
            <ul style={styles.ul}>
              <Box sx={styles.selectAllBox}>
                <span>
                  <CustomCheckbox
                    size="small"
                    onChange={() => toggleSelectAll("metasearch")}
                    checked={isSelectAllChecked}
                    title={label === "Channel" ? "Metasearch" : `All ${label}`}
                    labelStyles={styles.selectAllLabel}
                  />
                </span>
              </Box>
              <Box sx={styles.optionsBox}>
                <Box sx={styles.optionsColumn}>
                  {selected.map(item => (
                    <li key={item.id} style={styles.li}>
                      <div style={styles.liDiv}>
                        <Box sx={styles.liBox}>
                          <span style={styles.liSpan}>
                            <CustomCheckbox
                              size="small"
                              checked={item.isSelected}
                              title={item.label}
                              labelStyles={styles.checkboxLabel}
                              onChange={() => handleCheckboxChange(item.id ?? item.value)}
                            />
                          </span>
                        </Box>
                      </div>
                    </li>
                  ))}
                </Box>
              </Box>
            </ul>
            <div style={styles.adsContainer}>
              {optionsDesignation.length > 0 && (
                <ul>
                  <Box sx={styles.adsBox}>
                    <CustomCheckbox
                      size="small"
                      title="Destination Ads"
                      onChange={() => toggleSelectAll("DesignationAds")}
                      checked={isDesignatedAllChecked}
                      labelStyles={styles.adsLabel}
                    />
                  </Box>
                  <Box sx={styles.adsColumn}>
                    {selectedDesignation.map(item => (
                      <li key={item.id} style={styles.li}>
                        <div style={styles.liDiv}>
                          <Box sx={styles.liBox}>
                            <span style={styles.adsSpan}>
                              <CustomCheckbox
                                size="small"
                                checked={item.isSelected}
                                title={item.label}
                                sx={styles.checkboxSx}
                                onChange={() => handleDesignationChange(item.id)}
                              />
                            </span>
                          </Box>
                        </div>
                      </li>
                    ))}
                  </Box>
                </ul>
              )}
              {optionsOtherAds.length > 0 && (
                <ul>
                  <Box sx={styles.adsBox}>
                    <CustomCheckbox
                      size="small"
                      title="Other Ads"
                      onChange={() => toggleSelectAll("OtherAds")}
                      checked={isOtherAdsAllChecked}
                      labelStyles={styles.adsLabel}
                    />
                  </Box>
                  <Box sx={styles.adsColumn}>
                    {selectedOtherAds.map(item => (
                      <li key={item.id} style={styles.li}>
                        <div style={styles.liDiv}>
                          <Box sx={styles.liBox}>
                            <span style={styles.adsSpan}>
                              <CustomCheckbox
                                size="small"
                                checked={item.isSelected}
                                title={item.label}
                                sx={styles.checkboxSx}
                                onChange={() => handleOtherAdsChange(item.id)}
                              />
                            </span>
                          </Box>
                        </div>
                      </li>
                    ))}
                  </Box>
                </ul>
              )}
            </div>
          </div>
        </div>
        <Box sx={styles.footerBox}>
          <span
            style={styles.unselectAllSpan}
            className="sublabel"
            onClick={handleUnselectAll}
          >
            Unselect All
          </span>
          <Button
            variant="outlined"
            sx={styles.applyButton}
            onClick={handleApply}
          >
            Apply
          </Button>
        </Box>
      </Menu>
    </>
  );
};

export default FilterMenu;